import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

interface PermissionsContextValue {
  /** Array of all user permissions */
  permissions: string[]
  /** Check if user has a specific permission (e.g. 'knowledge:view') */
  hasPermission: (permission: string) => boolean
  /** Check if user has at least one of the given permissions */
  hasAnyPermission: (permissions: string[]) => boolean
  /** Whether permissions are still being loaded */
  isLoading: boolean
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null)

/**
 * Loads user permissions from Auth0 token claims once and shares them app-wide.
 * Permissions can be in either the ID token or access token claims.
 */
export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { getIdTokenClaims, getAccessTokenSilently, isAuthenticated, user } = useAuth0()
  const [permissions, setPermissions] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!isAuthenticated) {
        setPermissions([])
        setIsLoading(false)
        return
      }

      try {
        // Try to get permissions from both ID token and access token
        const [idClaims, accessToken] = await Promise.all([
          getIdTokenClaims().catch(() => null),
          getAccessTokenSilently().catch(() => null),
        ])

        // Decode access token to get permissions
        let accessTokenClaims = null
        if (accessToken) {
          try {
            // JWT tokens are base64 encoded, split by '.'
            const parts = accessToken.split('.')
            if (parts.length === 3) {
              accessTokenClaims = JSON.parse(atob(parts[1]))
            }
          } catch (e) {
            console.error('[PermissionsProvider] Failed to decode access token:', e)
          }
        }

        // Check multiple possible locations for permissions
        let userPermissions: string[] = []

        // 1. Check access token claims.permissions (most common)
        if (accessTokenClaims?.permissions && Array.isArray(accessTokenClaims.permissions)) {
          userPermissions = accessTokenClaims.permissions
        }
        // 2. Check ID token claims.permissions
        else if (idClaims?.permissions && Array.isArray(idClaims.permissions)) {
          userPermissions = idClaims.permissions
        }
        // 3. Check user metadata permissions
        else if (user?.permissions && Array.isArray(user.permissions)) {
          userPermissions = user.permissions
        }
        // 4. Check for permissions in custom namespace (both tokens)
        else {
          const allClaims = { ...idClaims, ...accessTokenClaims }
          for (const key of Object.keys(allClaims || {})) {
            const value = allClaims[key]
            if (key.includes('permissions') && Array.isArray(value)) {
              userPermissions = value as string[]
              break
            }
          }
        }

        setPermissions(userPermissions)
      } catch (error) {
        console.error('[PermissionsProvider] Failed to fetch permissions:', error)
        setPermissions([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchPermissions()
  }, [getIdTokenClaims, getAccessTokenSilently, isAuthenticated, user])

  const value = useMemo<PermissionsContextValue>(
    () => ({
      permissions,
      hasPermission: (permission: string) => permissions.includes(permission),
      hasAnyPermission: (candidates: string[]) => candidates.some((p) => permissions.includes(p)),
      isLoading,
    }),
    [permissions, isLoading]
  )

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
}

export function usePermissionsContext(): PermissionsContextValue {
  const context = useContext(PermissionsContext)
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider')
  }
  return context
}
